<template>
  <div class="w-full max-h-screen">
    <div v-if="showModal">
      <Modal
        v-for="message in modalMessages"
        :key="message.id"
        :id="message.id"
        @close="closeModal"
      >
        <template v-slot:header>{{ message.message }} </template>

        <template v-slot:body> {{ message.description }} </template>
      </Modal>
    </div>
    <div v-if="adminAndLocationRoles.includes(user.role)">
      <Dashboard />
    </div>
    <div v-else class="w-full p-5">
      <Welcome />
    </div>
    <InformationModal
      :show="showFreeTrialInfoModal"
      @update:show="showFreeTrialInfoModal = $event"
      @confirm="showFreeTrialInfoModal = false"
      :title="'Free trial subscription'"
      :content="getFreeTrialInfoContent()"
      :width="'700px'"
      :confirmationText="'Continue'"
      :isHtml="true"
    />
  </div>
</template>

<script setup>
import Modal from "@/components/general/Modal.vue";
import { locations } from "@/services/location/location.js";
import { useToast } from "vue-toastification";
import { useCookies } from "vue3-cookies";
import InformationModal from "@/components/profile/modals/InformationModal.vue";
import { getFreeTrialData, getListMessages } from "@/services/user/user.js";
import Dashboard from "@/views/Dashboard.vue";
import Welcome from "@/views/Welcome.vue";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { LOCATION_ROLES } from "@/utils/userRoles";

const toast = useToast();
const { cookies } = useCookies();
const route = useRoute();
const store = useStore();

const user = computed(() => store.getters["user/getUserData"]);

const messages = ref([]);
const showModal = ref(true);
const modalMessages = ref([]);
const cookiesMessages = ref([]);
const showFreeTrialInfoModal = ref(false);
const freeTrialRemainingDays = ref(0);
const adminAndLocationRoles = ref(["Mastermind_admin", ...LOCATION_ROLES]);

onMounted(async () => {
  const { isFreeTrial, fromPlayerRegistration } = route.query;

  if (fromPlayerRegistration === "true") {
    const { data } = await locations();
    const firstLocation = data?.locations?.[0];

    if (firstLocation) {
      store.commit("location/setLocationName", firstLocation.name);
      store.commit("location/setLocationLogo", firstLocation.logo_url);
    }
  }

  const freeTrialModalShowed = verifyFreeTrialModalShowed();

  if (isFreeTrial === "true" && !freeTrialModalShowed) {
    const freeTrialData = await getFreeTrialData();
    freeTrialRemainingDays.value = freeTrialData.remaining_days;
    showFreeTrialInfoModal.value = true;
    cookies.set("freeTrialModalShowed", true);
  }

  if (!adminAndLocationRoles.value.includes(user.value.role)) {
    if (route.query.messages) {
      getMessages();
    }
  }
});

function getMessages() {
  getListMessages().then((response) => {
    messages.value = response.data;
    filterMessages();
  });
}

function filterMessages() {
  verifyCookieExists();
  for (const message of messages.value) {
    if (message["concealable"] && !verifyMessageExists(message.id)) {
      modalMessages.value.push(message);
    }
  }
  if (modalMessages.value.length === 0) {
    showModal.value = false;
    showPopUpMessages();
  }
}

function showPopUpMessages() {
  for (const message of messages.value) {
    if (!message["concealable"]) {
      toast.info(message["message"], { timeout: false });
    }
  }
}

function closeModal(data) {
  if (data[1]) {
    cookiesMessages.value.push(data[0]);
  }
  modalMessages.value.pop();
  if (modalMessages.value.length === 0) {
    cookies.set("messages", JSON.stringify(cookiesMessages));
    showPopUpMessages();
  }
}

function verifyCookieExists() {
  cookiesMessages.value = JSON.parse(cookies.get("messages")) ?? [];
}

function verifyMessageExists(messageId) {
  return cookiesMessages.value.includes(messageId);
}

function verifyFreeTrialModalShowed() {
  return cookies.get("freeTrialModalShowed") === "true";
}

function getFreeTrialInfoContent() {
  return `Welcome and congratulations on starting your free ${freeTrialRemainingDays.value}-day trial. 
  You have access to the entire training program during this period; 
  if you have questions or need help, please reach out to 
  <a href="mailto:support@wetrainyourbrain.com" class="underline">support@wetrainyourbrain.com</a>.`;
}
</script>
